import React, { useEffect, useMemo, useState } from "react"
import { Button, Form, Input, Spin } from "antd"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useGet } from "utils/useFetch"
import { ewaAxiosInstance } from "utils/request"
import { Button as CustomButton } from "components"
import { debounce } from "lodash"
import type {
  IDutyLocationDoc,
  ISetupDutyLocationRes,
  IWorkplaceByIdRes,
  IWorkplaceDetailDoc,
} from "../../typings"
import { SETUP_WORKPLACE_QUERY_KEY } from "../../typings"
import CustomModal from "components/new-stuff/ui/modals/CustomModal"
import CustomInput from "./CustomInput"
import {
  ChevronLeftIcon,
  MagnifyingGlassIcon,
  PlusIcon,
} from "@heroicons/react/24/outline"
import GeoPicker from "components/GeoPicker"
import { calculateRadius } from "../../utils/shiftTablesUtils"
import DutyLocationCard from "../../components/DutyLocationCard"
import { CustomBackButton } from "../Setup"
import CustomEmptyState from "../../overview/component/CustomEmptyState"
import { onQueryError } from "utils/utils"
import { useAppNotificationStore } from "stores"
import usePermissions from "hooks/usePermissions"
import { TimeAndAttendancePermission } from "constants/permissions"

// ─── Domain types ─────────────────────────────────────────────────────────────

export interface CreateDutyLocationBody {
  name: string
  addressLine1: string
  longitude?: string
  latitude?: string
  addressCity?: string
  addressState?: string
  stateCode?: string
  zipCode?: string
  countryCode?: string
  country?: string
}

export interface LocationFormValues {
  locationName: string
  locationAddress: string
  longitude?: number
  latitude?: number
  radius?: number
  addressCity?: string
  addressState?: string
  stateCode?: string
  zipCode?: string
  countryCode?: string
  country?: string
}

interface AddressComponent {
  types: string[]
  short_name: string
  long_name: string
}

interface PlaceDetails {
  formatted_address?: string
  address_components?: AddressComponent[]
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  workplaceId: string
  onBack: () => void
  onNext: () => void
  isSavingProgress?: boolean
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function findComponent(
  components: AddressComponent[],
  type: string
): AddressComponent | undefined {
  return components.find((c) => c.types?.includes(type))
}

// ─── Component ────────────────────────────────────────────────────────────────

const SetupDutyLocations: React.FC<Props> = ({
  workplaceId,
  onBack,
  onNext,
  isSavingProgress = false,
}) => {
  const [searchInput, setSearchInput] = useState("")
  const [keyword, setKeyword] = useState("")
  const queryClient = useQueryClient()

  const { toast } = useAppNotificationStore()
  const [canManageDutyLocations] = usePermissions({
    allowedPermissions: [TimeAndAttendancePermission.CanManageDutyLocations],
  })

  const debouncedSetSearch = useMemo(
    () => debounce((value: string) => setKeyword(value.trim()), 500),
    []
  )

  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch])

  // ── Queries ────────────────────────────────────────────────────────────────

  const dutyLocationQueryKey = [SETUP_WORKPLACE_QUERY_KEY, workplaceId, keyword]

  const { data: dutyLocations, isLoading: isDutyLocationsLoading } =
    useGet<ISetupDutyLocationRes>({
      url: `/v1/time-attendance/workplaces/${workplaceId}/duty-locations${
        keyword ? `?keyword=${keyword}` : ""
      }`,
      queryKey: dutyLocationQueryKey,
      instanceKey: "ewaAxiosInstance",
      enabled: !!workplaceId,
      refetchOnMount: false,
    })

  const { data: workplaceRes, isLoading: isWorkplaceLoading } =
    useGet<IWorkplaceByIdRes>({
      url: `/v1/time-attendance/workplaces/${workplaceId}`,
      queryKey: [SETUP_WORKPLACE_QUERY_KEY, workplaceId],
      instanceKey: "ewaAxiosInstance",
      enabled: !!workplaceId,
      refetchOnMount: false,
    })

  const workplace: IWorkplaceDetailDoc | undefined = workplaceRes?.data

  // ── Mutations ──────────────────────────────────────────────────────────────

  const refetchDutyLocations = () =>
    queryClient.invalidateQueries({ queryKey: dutyLocationQueryKey })

  const { mutate: createDutyLocation, isLoading: isCreatingLocation } =
    useMutation({
      mutationFn: async (body: CreateDutyLocationBody) => {
        const res = await ewaAxiosInstance.post(
          `/v1/time-attendance/workplaces/${workplaceId}/duty-locations`,
          body
        )
        return res.data
      },
      onSuccess: () => {
        refetchDutyLocations()
        closeLocationModal()
        toast.success("Duty Location Created Successfully")
      },
    })

  const { mutate: updateDutyLocation, isLoading: isUpdatingLocation } =
    useMutation({
      mutationFn: async ({
        dutyLocationId,
        body,
      }: {
        dutyLocationId: number
        body: CreateDutyLocationBody
      }) => {
        const res = await ewaAxiosInstance.put(
          `/v1/time-attendance/workplaces/${workplaceId}/duty-locations/${dutyLocationId}`,
          body
        )
        return res.data
      },
      onSuccess: () => {
        refetchDutyLocations()
        closeLocationModal()
      },
      onError: onQueryError,
    })

  const { mutate: deleteDutyLocation, isLoading: isDeletingDutyLocation } =
    useMutation({
      mutationFn: async (dutyLocationId: number) => {
        const res = await ewaAxiosInstance.delete(
          `/v1/time-attendance/workplaces/${workplaceId}/duty-locations/${dutyLocationId}`
        )
        return res.data
      },
      onSuccess: () => {
        toast.success("Delete Successful")
        refetchDutyLocations()
      },
      onError: onQueryError,
    })

  // ── Modal state ────────────────────────────────────────────────────────────

  const [locationModalOpen, setLocationModalOpen] = useState(false)
  const [editingLocation, setEditingLocation] =
    useState<IDutyLocationDoc | null>(null)
  const [locationForm] = Form.useForm<LocationFormValues>()

  const openCreateLocationModal = () => {
    if (!canManageDutyLocations) {
      toast.unauthorized()
      return
    }
    setEditingLocation(null)
    locationForm.resetFields()
    setLocationModalOpen(true)
  }

  const openEditLocationModal = (loc: IDutyLocationDoc) => {
    if (!canManageDutyLocations) {
      toast.unauthorized()
      return
    }
    setEditingLocation(loc)
    locationForm.setFieldsValue({
      locationName: loc.name,
      locationAddress: loc.addressLine1,
      latitude: loc.latitude != null ? Number(loc.latitude) : undefined,
      longitude: loc.longitude != null ? Number(loc.longitude) : undefined,
      addressCity: loc.addressCity ?? "",
      addressState: loc.addressState ?? "",
      stateCode: loc.stateCode ?? "",
      zipCode: loc.zipCode ?? "",
      countryCode: loc.countryCode ?? "",
      country: loc.country ?? "",
    })
    setLocationModalOpen(true)
  }

  const closeLocationModal = () => {
    setLocationModalOpen(false)
    setEditingLocation(null)
    locationForm.resetFields()
  }

  // ── Form handlers ──────────────────────────────────────────────────────────

  const handleLocationFinish = (values: LocationFormValues) => {
    // Merge onFinish values with current form state so GeoPicker-populated
    // hidden fields are always included.
    const merged: LocationFormValues = {
      ...locationForm.getFieldsValue(),
      ...values,
    }

    const body: CreateDutyLocationBody = {
      name: merged.locationName,
      addressLine1: merged.locationAddress,
      addressCity: merged.addressCity,
      addressState: merged.addressState,
      stateCode: merged.stateCode,
      countryCode: merged.countryCode,
      country: merged.country,
      zipCode: merged.zipCode,
      longitude:
        merged.longitude != null ? String(merged.longitude) : undefined,
      latitude: merged.latitude != null ? String(merged.latitude) : undefined,
    }

    if (editingLocation) {
      updateDutyLocation({ dutyLocationId: editingLocation.id, body })
    } else {
      createDutyLocation(body)
    }
  }

  const handlePlaceDetails = (place: PlaceDetails) => {
    try {
      const comps = place.address_components ?? []

      const countryComp = findComponent(comps, "country")
      const stateComp = findComponent(comps, "administrative_area_level_1")
      const cityComp =
        findComponent(comps, "locality") ??
        findComponent(comps, "postal_town") ??
        findComponent(comps, "administrative_area_level_2")
      const postalComp = findComponent(comps, "postal_code")

      const formattedAddress =
        place.formatted_address ??
        locationForm.getFieldValue("locationAddress") ??
        ""

      locationForm.setFieldsValue({
        locationAddress: formattedAddress,
        countryCode: countryComp?.short_name ?? "",
        country: countryComp?.long_name ?? "",
        addressCity: cityComp?.long_name ?? cityComp?.short_name ?? "",
        addressState: stateComp?.long_name ?? stateComp?.short_name ?? "",
        stateCode: stateComp?.short_name ?? "",
        zipCode: postalComp?.long_name ?? postalComp?.short_name ?? "",
      })
    } catch {
      // Silently ignore address parse failures
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  const docs = dutyLocations?.data?.docs ?? []
  const isEditing = !!editingLocation
  const isPrimaryFullWidth = (1 + docs.length) % 2 !== 0

  return (
    <>
      {/* Header (static) */}
      <div className="flex flex-col gap-4 shrink-0">
          <CustomBackButton onBack={onBack} />

          <div className="flex flex-col gap-2 text-[#0F172B]">
            <h5 className="text-2xl font-bold">
              Select duty locations to configure
            </h5>
            <p className="text-sm">
              Review and configure the duty locations for this workplace.
            </p>
          </div>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-6 pt-6 pr-2">
          {/* Search */}
          <div className="[&_.ant-input-affix-wrapper]:!h-10">
          <Input
            placeholder="Search for a location"
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value)
              debouncedSetSearch(e.target.value)
            }}
            prefix={<MagnifyingGlassIcon className="w-4 h-4 text-[#94A3B8]" />}
            className="h-10 rounded-lg"
          />
        </div>

        {/* List */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between gap-4">
              <p className="font-bold text-[16px] text-[#0F172B]">
                Duty locations for{" "}
                <WorkplaceName name={workplace?.displayName ?? ""} />
              </p>
              <Button
                type="default"
                className="font-semibold px-4 h-10 rounded-md border-2 border-[#004AF5] text-[#004AF5] bg-white hover:!border-[#004AF5] hover:!text-[#004AF5] hover:!bg-[#F8FAFC] flex items-center gap-2"
                onClick={openCreateLocationModal}
              >
                <PlusIcon className="w-4 h-4" />
                Add duty location
              </Button>
            </div>
            <p className="text-sm text-slate-500">
              A duty location is where employees physically show up and clock
              in.
            </p>
          </div>

          {/* Primary workplace address — read-only.
              Renders full-width above the grid when the total card count
              (Primary + others) is odd; otherwise it joins the grid as the
              first cell so rows stay paired. */}
          {isPrimaryFullWidth && (
            <DutyLocationCard
              title="Primary"
              location={workplace?.fullAddress ?? ""}
            />
          )}

          <Spin
            spinning={
              isDutyLocationsLoading ||
              isWorkplaceLoading ||
              isDeletingDutyLocation
            }
          >
            {docs.length === 0 && !isDutyLocationsLoading ? (
              <CustomEmptyState
                imageUrl="https://padehrs3-staging.s3.us-east-2.amazonaws.com/images/bec76e87-356c-45da-bc4d-46f6cf1ea935.png"
                onClick={openCreateLocationModal}
                title="Add duty location"
                top="80"
              />
            ) : (
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {!isPrimaryFullWidth && (
                  <DutyLocationCard
                    title="Primary"
                    location={workplace?.fullAddress ?? ""}
                  />
                )}
                {docs.map((doc) => (
                  <DutyLocationCard
                    key={doc.id}
                    title={doc.name}
                    location={doc.addressLine1}
                    onEdit={() =>
                      openEditLocationModal(doc as IDutyLocationDoc)
                    }
                    onDelete={() => {
                      if (!canManageDutyLocations) {
                        toast.unauthorized()
                        return
                      }
                      deleteDutyLocation(doc.id)
                    }}
                    editIcon={
                      <svg
                        width="13"
                        height="13"
                        viewBox="0 0 13 13"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g clipPath="url(#clip0_7059_12865)">
                          <path
                            d="M4.70691 10.9688H2.4375C2.32976 10.9688 2.22642 10.926 2.15024 10.8498C2.07405 10.7736 2.03125 10.6703 2.03125 10.5626V8.29315C2.0313 8.18555 2.07404 8.08237 2.15008 8.00624L8.41242 1.74389C8.4886 1.66776 8.59189 1.625 8.69959 1.625C8.80729 1.625 8.91058 1.66776 8.98676 1.74389L11.2562 4.01178C11.3323 4.08796 11.3751 4.19125 11.3751 4.29895C11.3751 4.40665 11.3323 4.50994 11.2562 4.58612L4.99383 10.85C4.9177 10.926 4.81451 10.9688 4.70691 10.9688Z"
                            stroke="#90A1B9"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M6.90625 3.25L9.75 6.09375"
                            stroke="#90A1B9"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_7059_12865">
                            <rect
                              width="13"
                              height="13"
                              fill="white"
                            />
                          </clipPath>
                        </defs>
                      </svg>
                    }
                    deleteIcon={
                      <svg
                        width="13"
                        height="13"
                        viewBox="0 0 13 13"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M3.4043 4.64307L4.02334 10.8335H8.97573L9.59477 4.64307"
                          stroke="#90A1B9"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M7.3125 8.39583V5.6875"
                          stroke="#90A1B9"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M5.6875 8.39583V5.6875"
                          stroke="#90A1B9"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M2.47656 3.4046H4.95275M4.95275 3.4046L5.1597 2.5768C5.21999 2.33566 5.43664 2.1665 5.6852 2.1665H7.31555C7.5641 2.1665 7.78076 2.33566 7.84104 2.5768L8.04799 3.4046M4.95275 3.4046H8.04799M8.04799 3.4046H10.5242"
                          stroke="#90A1B9"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    }
                  />
                ))}
              </ul>
            )}
          </Spin>
        </div>
      </div>

      {/* Footer */}
      <div className="shrink-0 pt-4 border-t border-[#E2E8F0] flex justify-end">
        <CustomButton
          color="primary"
          className="h-10 rounded-md font-semibold"
          onClick={onNext}
          submitting={isSavingProgress}
          disabled={isSavingProgress}
        >
          Next step
        </CustomButton>
      </div>

      {/* Create / Edit modal */}
      <CustomModal
        rootClassName="ta-scope"
        open={locationModalOpen}
        onCancel={closeLocationModal}
        title={isEditing ? "Edit duty location" : "Create duty location"}
        width={444}
        centered
        footer={null}
      >
        <Form
          form={locationForm}
          layout="vertical"
          onFinish={handleLocationFinish}
          className="flex flex-col gap-3"
        >
          <CustomInput
            label="Duty location name"
            name="locationName"
            control="input"
            placeholder="Enter duty location name"
            rules={[
              { required: true, message: "Please enter duty location name" },
            ]}
            className="h-10"
          />
          <div></div>
          <Form.Item
            label="Duty location address"
            name="locationAddress"
            rules={[
              {
                required: true,
                message: "Please enter duty location address",
              },
            ]}
          >
            <GeoPicker
              value={locationForm.getFieldValue("locationAddress")}
              onChange={(address: string) =>
                locationForm.setFieldsValue({ locationAddress: address })
              }
              onSelect={(lat: number, lng: number) => {
                const radius = calculateRadius(lat, lng)
                locationForm.setFieldsValue({
                  latitude: lat,
                  longitude: lng,
                  radius,
                })
              }}
              onPlaceDetails={handlePlaceDetails}
            />
          </Form.Item>

          {/* Hidden geo + address fields */}
          {(
            [
              "latitude",
              "longitude",
              "radius",
              "addressCity",
              "addressState",
              "stateCode",
              "zipCode",
              "countryCode",
              "country",
            ] as const
          ).map((name) => (
            <Form.Item
              key={name}
              name={name}
              noStyle
            >
              <Input type="hidden" />
            </Form.Item>
          ))}

          <Form.Item className="mb-0 mt-4">
            <Button
              type="primary"
              htmlType="submit"
              loading={isEditing ? isUpdatingLocation : isCreatingLocation}
              className="font-semibold h-10 px-6 rounded-md bg-[#004AF5] w-full"
            >
              {isEditing ? "Save duty location" : "Create duty location"}
            </Button>
          </Form.Item>
        </Form>
      </CustomModal>
    </>
  )
}

export default SetupDutyLocations

// ─── Sub-components ───────────────────────────────────────────────────────────

export const WorkplaceName: React.FC<{ name: string }> = ({ name }) => (
  <span className="inline-flex items-center rounded-md h-max px-1.5 bg-slate-50 border border-[#CAD5E2] font-bold text-[#0F172B]">
    {name}
  </span>
)
